import React, { useEffect, useMemo, useState } from "react";
import { BottomSheet } from "../../../shared/components/BottomSheet.jsx";
import { Button } from "../../../shared/components/Button.jsx";
import { openBillsFor, nameScore, suggestForParty } from "../utils/matcher.js";
import { CATEGORIES } from "../services/matchService.js";

const inr = (v) => `₹${Number(v || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;

/**
 * "Who is this?" — pick the party for a bank line (with dues and ranked suggestions), choose how to settle
 * (oldest bills first or chosen bills), or mark it as a non-party line (own transfer, charges, …).
 */
export function MatchSheet({ isOpen, onClose, txn, suggestion, ctx, fmt, onApply }) {
  const direction = txn?.creditPaise ? "in" : "out";
  const [search, setSearch] = useState("");
  const [party, setParty] = useState(null);
  const [mode, setMode] = useState("fifo"); // fifo | choose | link
  const [picked, setPicked] = useState(new Set());
  const [remember, setRemember] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setSearch("");
    setParty(suggestion?.party || null);
    setMode(suggestion?.action === "link" ? "link" : "fifo");
    setPicked(new Set());
    setRemember(true);
  }, [isOpen, suggestion]);

  const parties = useMemo(() => {
    if (!ctx || !txn) return [];
    const wanted = direction === "in" ? ["customer", "both"] : ["vendor", "both"];
    const term = search.trim().toLowerCase();
    const name = suggestion?.parsed?.name || "";
    return ctx.parties
      .map((p) => {
        const due = openBillsFor(p, direction, ctx).reduce((s, b) => s + Number(b.balance || 0), 0);
        const typeOk = wanted.includes(p.partyType || "customer");
        return { p, due, typeOk, score: nameScore(name, p.name) + (typeOk ? 10 : 0) + (due > 0 ? 5 : 0) };
      })
      .filter(({ p, typeOk }) => (term ? `${p.name} ${p.phone || ""}`.toLowerCase().includes(term) : typeOk))
      .sort((a, b) => b.score - a.score || b.due - a.due || String(a.p.name).localeCompare(String(b.p.name)))
      .slice(0, 30);
  }, [ctx, txn, direction, search, suggestion]);

  const partySuggestion = useMemo(() => (party && txn ? suggestForParty(txn, party, ctx) : null), [party, txn, ctx]);
  const bills = partySuggestion?.bills || [];
  const chosen = useMemo(() => {
    if (!party || !txn) return null;
    if (mode === "choose") return suggestForParty(txn, party, ctx, { bills: bills.filter((b) => picked.has(b.id)) });
    if (mode === "fifo" && partySuggestion?.action === "link") return suggestForParty(txn, party, ctx, { bills });
    return partySuggestion;
  }, [mode, party, txn, ctx, bills, picked, partySuggestion]);

  if (!txn) return null;
  const amount = (txn.creditPaise || txn.debitPaise) / 100;
  const noun = direction === "in" ? "receipt" : "payment";

  const apply = async (choice, rememberIt = remember) => {
    setSaving(true);
    try {
      await onApply(choice, rememberIt);
      onClose();
    } catch (err) {
      alert(err.message || "Could not save the match.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <BottomSheet isOpen={isOpen} onClose={onClose} title="Who is this?">
      <div style={{ background: "var(--bg-subtle, var(--bg-card))", border: "1px solid var(--border-subtle)", borderRadius: "var(--radius-md)", padding: "0.5rem 0.7rem", marginBottom: "0.75rem" }}>
        <div style={{ fontWeight: 800, color: direction === "in" ? "var(--success)" : "var(--danger)" }}>
          {direction === "in" ? "+" : "−"}{inr(amount)} <span style={{ fontWeight: 500, fontSize: "0.78rem", color: "var(--text-dim)" }}>📅 {fmt(txn.date)}</span>
        </div>
        <div style={{ fontSize: "0.78rem", color: "var(--text-main)", wordBreak: "break-word" }}>{txn.description}</div>
      </div>

      {!party ? (
        <>
          <div className="search-container" style={{ marginBottom: "0.5rem" }}>
            <span className="search-icon">🔍</span>
            <input type="text" className="search-input" placeholder={`Search ${direction === "in" ? "customers" : "vendors"} (all parties when typing)…`} value={search} onChange={(e) => setSearch(e.target.value)} autoFocus />
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: "0.3rem", maxHeight: "38vh", overflowY: "auto", marginBottom: "0.75rem" }}>
            {parties.length === 0 && <div style={{ fontSize: "0.8rem", color: "var(--text-dim)", padding: "0.5rem" }}>No parties found.</div>}
            {parties.map(({ p, due }) => (
              <button key={p.id} type="button" onClick={() => { setParty(p); setMode("fifo"); }}
                style={{ display: "flex", justifyContent: "space-between", gap: "0.5rem", textAlign: "left", padding: "0.5rem 0.7rem", background: "var(--bg-card)", color: "var(--text-main)", border: "1px solid var(--border-subtle)", borderRadius: "var(--radius-md)", cursor: "pointer" }}>
                <span style={{ fontWeight: 600, fontSize: "0.85rem" }}>{p.name} <span style={{ fontWeight: 400, fontSize: "0.72rem", color: "var(--text-dim)" }}>({p.partyType || "customer"})</span></span>
                <span style={{ fontSize: "0.75rem", color: due > 0 ? "var(--warning, #b45309)" : "var(--text-dim)", flexShrink: 0 }}>{due > 0 ? `due ${inr(due)}` : "no dues"}</span>
              </button>
            ))}
          </div>
        </>
      ) : (
        <div style={{ marginBottom: "0.75rem" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.5rem" }}>
            <div style={{ fontWeight: 700 }}>{party.name}</div>
            <button type="button" className="btn btn-secondary btn-sm" onClick={() => setParty(null)}>Change</button>
          </div>

          {partySuggestion?.action === "link" && (
            <label style={{ display: "flex", gap: "0.5rem", alignItems: "flex-start", fontSize: "0.82rem", marginBottom: "0.4rem" }}>
              <input type="radio" checked={mode === "link"} onChange={() => setMode("link")} />
              <span>🔗 Link to <b>{partySuggestion.payment.paymentNo}</b> ({fmt(partySuggestion.payment.paymentDate)}, {inr(partySuggestion.payment.amount)}) — already recorded, nothing changes in the books</span>
            </label>
          )}
          <label style={{ display: "flex", gap: "0.5rem", alignItems: "center", fontSize: "0.82rem", marginBottom: "0.4rem" }}>
            <input type="radio" checked={mode === "fifo"} onChange={() => setMode("fifo")} />
            <span>New {noun}, settle oldest bills first</span>
          </label>
          {bills.length > 0 && (
            <label style={{ display: "flex", gap: "0.5rem", alignItems: "center", fontSize: "0.82rem", marginBottom: "0.4rem" }}>
              <input type="radio" checked={mode === "choose"} onChange={() => setMode("choose")} />
              <span>New {noun}, choose bills</span>
            </label>
          )}

          {mode === "choose" && (
            <div style={{ display: "flex", flexDirection: "column", gap: "0.25rem", margin: "0.25rem 0 0.5rem 1.4rem" }}>
              {bills.map((b) => (
                <label key={b.id} style={{ display: "flex", gap: "0.5rem", alignItems: "center", fontSize: "0.8rem" }}>
                  <input type="checkbox" checked={picked.has(b.id)} onChange={() => setPicked((s) => { const n = new Set(s); n.has(b.id) ? n.delete(b.id) : n.add(b.id); return n; })} />
                  <span>{b.invoiceNo} • {fmt(b.invoiceDate)} • due {inr(b.balance)}</span>
                </label>
              ))}
            </div>
          )}

          {chosen && mode !== "link" && (
            <div style={{ fontSize: "0.78rem", color: "var(--text-dim)", margin: "0.25rem 0 0.5rem" }}>
              {chosen.allocations.length
                ? chosen.allocations.map((a) => `${a.invoiceNo} ${inr(a.allocatedAmount)}`).join(", ")
                : "No bills settled"}
              {chosen.unallocatedAmount > 0 && ` • ${inr(chosen.unallocatedAmount)} kept as advance`}
            </div>
          )}

          {suggestion?.parsed?.key && (
            <label style={{ display: "flex", gap: "0.5rem", alignItems: "center", fontSize: "0.8rem", marginBottom: "0.6rem" }}>
              <input type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)} />
              <span>Remember "{suggestion.parsed.name}" = {party.name}</span>
            </label>
          )}

          <Button variant="primary" style={{ width: "100%" }} disabled={saving || (mode === "choose" && picked.size === 0)}
            onClick={() => apply(mode === "link" ? partySuggestion : chosen)}>
            {saving ? "Saving…" : mode === "link" ? `🔗 Link to ${partySuggestion?.payment?.paymentNo}` : `Record ${noun} ${inr(amount)}`}
          </Button>
        </div>
      )}

      <div style={{ fontSize: "0.72rem", fontWeight: 700, color: "var(--text-muted)", textTransform: "uppercase", margin: "0.5rem 0 0.35rem" }}>Not a party payment</div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: "0.35rem" }}>
        {CATEGORIES.map((c) => (
          <button key={c.key} type="button" className="btn btn-secondary btn-sm" disabled={saving}
            onClick={() => apply({ action: "category", category: c.key, direction }, false)}>
            {c.icon} {c.label}
          </button>
        ))}
      </div>
    </BottomSheet>
  );
}

export default MatchSheet;
